// app/pages/activities/[id].vue

<template>
  <div class="activity-detail-page">
    <!-- Loading State -->
    <div v-if="pending" class="loading-container">
      <div class="loading-spinner"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-content">
        <h2>{{ locale === 'zh' ? '加载失败' : locale === 'en' ? 'Loading Failed' : 'Gagal Memuat' }}</h2>
        <p>{{ error.message }}</p>
        <button @click="navigateTo('/activities')" class="back-btn">
          {{ locale === 'zh' ? '返回列表' : locale === 'en' ? 'Back to List' : 'Kembali ke Daftar' }}
        </button>
      </div>
    </div>

    <!-- Content -->
    <div v-else-if="activity" class="activity-detail">
      <!-- Activity Content -->
      <section class="detail-content">
        <div class="container">
          <!-- Breadcrumb -->
          <!-- <div class="breadcrumb">
            <a @click="navigateTo('/activities')" class="breadcrumb-link">
              {{ locale === 'zh' ? '品牌动态' : locale === 'en' ? 'Brand Dynamics' : 'Brand News' }}
            </a>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">{{ activity.title }}</span>
          </div> -->

          <!-- Title & Meta -->
          <div class="detail-header">
            <!-- API now returns string directly -->
            <h1 class="detail-title">{{ activity.title }}</h1>
            <div class="detail-meta">
              <span class="meta-item">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />
                </svg>
                {{ activity.date }}
              </span>
              <span class="meta-item" v-if="activity.location">
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
                </svg>
                <!-- API now returns string directly -->
                {{ activity.location }}
              </span>
            </div>
          </div>

          <!-- Description -->
          <div v-if="activity.description" class="detail-description">
            <!-- API now returns string directly -->
            <p>{{ activity.description }}</p>
          </div>

          <!-- Rich Text Content (if exists) -->
          <!-- Using activity.richText directly as string -->
          <div v-if="activity.richText" class="rich-text-content" v-html="activity.richText"></div>

          <!-- Image Gallery -->
          <!-- <div v-if="activity.imgList && activity.imgList.length" class="image-gallery">
            <h2 class="gallery-title">
              {{ locale === 'zh' ? '精彩瞬间' : locale === 'en' ? 'Highlights' : 'Sorotan' }}
            </h2>
            <div class="gallery-grid">
              <div v-for="(img, index) in activity.imgList" :key="index" class="gallery-item" @click="openGallery(index + 1)">
                <el-image :src="img" fit="cover" class="gallery-image" alt="" />
              </div>
            </div>
          </div> -->

          <!-- Back Button -->
          <div class="detail-actions">
            <button @click="navigateTo('/activities')" class="back-button">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
              </svg>
              {{ locale === 'zh' ? '返回列表' : locale === 'en' ? 'Back to List' : 'Kembali ke Daftar' }}
            </button>
          </div>
        </div>
      </section>

      <!-- Image Gallery Component -->
      <ImageGallery v-model="galleryVisible" :images="allImages" :initial-index="galleryIndex" :title="activity.title" @close="galleryVisible = false" />
    </div>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue'
  console.log('Activity Detail Page')
  const route = useRoute()
  const { locale } = useI18n()
  const activityId = route.params.id

  // 使用 useAsyncData 获取详情数据，支持SSR
  const {
    data: activityData,
    pending,
    error
  } = await useAsyncData(
    `activity-${activityId}`,
    async () => {
      const response = await $fetch('/api/banner/getDetail', {
        method: 'GET',
        params: {
          id: activityId,
          locale: locale.value
        }
      })
      return response
    },
    {
      watch: [locale] // 关键修改：监听 locale 变化，自动触发重新请求
    }
  )

  const activity = computed(() => {
    if (activityData.value?.success && activityData.value?.data) {
      return activityData.value.data
    }
    // 跳转404
    showError({
      statusCode: 404,
      statusMessage: '该文章不存在', // 这个信息会显示在 error.vue 的 props.error.message 中
      fatal: true // 强制显示错误页面（通常建议加上）
    })
    return null
  })

  // 设置页面标题
  useHead(() => ({
    // 移除 [locale.value]，直接使用字符串
    title: activity.value ? `${activity.value.title} - SANFU` : '活动详情 - SANFU',
    meta: [
      {
        name: 'description',
        content: activity.value ? activity.value.description : '品牌动态详情'
      }
    ]
  }))

  // 画廊相关状态
  const galleryVisible = ref(false)
  const galleryIndex = ref(0)

  // 所有图片（主图 + 图片列表）
  const allImages = computed(() => {
    if (!activity.value) return []
    const images = [activity.value.img]
    if (activity.value.imgList && Array.isArray(activity.value.imgList)) {
      images.push(...activity.value.imgList)
    }
    return images
  })

  // 打开画廊
  const openGallery = (index = 0) => {
    galleryIndex.value = index
    galleryVisible.value = true
  }
</script>

<style scoped lang="less">
  .activity-detail-page {
    min-height: 100vh;
    background-color: var(--gray-50);
  }

  .loading-container,
  .error-container {
    min-height: 60vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 4px solid var(--gray-200);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .error-content {
    text-align: center;
    padding: var(--space-8);
  }

  .error-content h2 {
    font-size: var(--text-2xl);
    color: var(--gray-900);
    margin-bottom: var(--space-4);
  }

  .error-content p {
    color: var(--gray-600);
    margin-bottom: var(--space-6);
  }

  .detail-hero {
    padding-top: var(--space-12);
    background: linear-gradient(0deg, var(--gray-100) 0%, var(--white) 100%);
  }

  .hero-image-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--space-6);
  }

  .hero-image {
    width: 100%;
    max-height: 500px;
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-lg);
  }

  .detail-content {
    padding: var(--space-12) 0;
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-8);
    font-size: var(--text-sm);
    color: var(--gray-600);
  }

  .breadcrumb-link {
    flex-shrink: 0;
    color: var(--primary);
    cursor: pointer;
    transition: color var(--transition-fast);
  }

  .breadcrumb-link:hover {
    color: var(--primary-dark);
    text-decoration: underline;
  }

  .breadcrumb-separator {
    color: var(--gray-400);
  }

  .breadcrumb-current {
    color: var(--gray-700);
  }

  .detail-header {
    margin-bottom: var(--space-8);
  }

  .detail-title {
    font-size: var(--text-4xl);
    font-weight: 700;
    color: var(--gray-900);
    margin-bottom: var(--space-4);
    line-height: 1.2;
  }

  .detail-meta {
    display: flex;
    gap: var(--space-6);
    color: var(--gray-600);
  }

  .meta-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-base);
  }

  .meta-item svg {
    width: 20px;
    height: 20px;
    color: var(--gray-500);
  }

  .detail-description {
    background-color: var(--white);
    padding: var(--space-8);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
    margin-bottom: var(--space-8);
  }

  .detail-description p {
    font-size: var(--text-lg);
    line-height: 1.8;
    color: var(--gray-700);
  }

  .rich-text-content {
    background-color: var(--white);
    padding: var(--space-8);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
    margin-bottom: var(--space-8);
    img {
      max-width: 100%;
      height: auto;
      border-radius: var(--radius);
      margin: var(--space-4) 0;
    }
  }

  .rich-text-content :deep(h1),
  .rich-text-content :deep(h2),
  .rich-text-content :deep(h3) {
    margin-top: var(--space-6);
    margin-bottom: var(--space-4);
    color: var(--gray-900);
  }

  .rich-text-content :deep(p) {
    margin-bottom: var(--space-4);
    line-height: 1.8;
    color: var(--gray-700);
  }

  .rich-text-content :deep(img) {
    width: 100%;
    max-width: 100%;
    border-radius: var(--radius);
    margin: var(--space-4) 0;
  }

  .image-gallery {
    background-color: var(--white);
    padding: var(--space-8);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow);
    margin-bottom: var(--space-8);
  }

  .gallery-title {
    font-size: var(--text-2xl);
    font-weight: 600;
    color: var(--gray-900);
    margin-bottom: var(--space-6);
  }

  .gallery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: var(--space-4);
  }

  .gallery-item {
    position: relative;
    padding-top: 75%;
    cursor: pointer;
    border-radius: var(--radius);
    overflow: hidden;
    transition: transform var(--transition-base);
  }

  .gallery-item:hover {
    transform: scale(1.05);
  }

  .gallery-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .detail-actions {
    display: flex;
    justify-content: center;
    padding: var(--space-8) 0;
  }

  .back-button,
  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-6);
    background-color: var(--primary);
    color: var(--white);
    border: none;
    border-radius: var(--radius);
    font-size: var(--text-base);
    font-weight: 500;
    cursor: pointer;
    transition: background-color var(--transition-fast);
  }

  .back-button:hover,
  .back-btn:hover {
    background-color: var(--primary-dark);
  }

  .back-button svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    .detail-title {
      font-size: var(--text-2xl);
    }

    .detail-meta {
      flex-direction: column;
      gap: var(--space-2);
    }

    .hero-image {
      max-height: 300px;
    }

    .gallery-grid {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    }
  }
</style>
